/**
 * Utility functions for storing and retrieving quote data in localStorage
 */

export interface QuoteData {
  fiatAmount: number;
  cryptoAmount: number;
  fiatCode: string;
  coinCode: string;
  paymentMethodId: string;
  processingFee: number;
  networkFee: number;
  blockchain?: string;
}

const QUOTE_KEY_PREFIX = 'quote_';

/**
 * Get the localStorage key for a quote
 */
function getQuoteKey(cardId: string): string {
  return `${QUOTE_KEY_PREFIX}${cardId}`;
}

/**
 * Get all quote keys from localStorage
 */
function getAllQuoteKeys(): string[] {
  const keys: string[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(QUOTE_KEY_PREFIX)) {
        keys.push(key);
      }
    }
  } catch (error) {
    console.error('Error getting quote keys from localStorage:', error);
  }
  return keys;
}

/**
 * Save quote data to localStorage
 * Removes ALL quotes (including the one being saved if it exists) before saving the new one
 * Ensures only one quote exists in localStorage at any time
 */
export function saveQuote(cardId: string, quoteData: QuoteData): void {
  try {
    // Remove ALL quotes from localStorage (including the one being saved if it exists)
    const keysToRemove = getAllQuoteKeys();
    
    // Remove all quote keys
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });

    const key = getQuoteKey(cardId);
    localStorage.setItem(key, JSON.stringify(quoteData));

    // Validate that only one quote exists after save
    const remainingQuotes = getAllQuoteKeys();
    if (remainingQuotes.length > 1) {
      console.warn(`⚠️ Multiple quotes detected after save! Found ${remainingQuotes.length} quotes. Cleaning up...`);
      ensureSingleQuote();
    } else if (remainingQuotes.length === 0) {
      console.warn('⚠️ No quote found after save! This should not happen.');
    }
  } catch (error) {
    console.error('Error saving quote to localStorage:', error);
    // Don't throw - allow navigation to continue even if storage fails
  }
}

/**
 * Retrieve quote data from localStorage by cardId
 */
export function getQuote(cardId: string): QuoteData | null {
  try {
    const key = getQuoteKey(cardId);
    const stored = localStorage.getItem(key);
    if (!stored) {
      return null;
    }
    return JSON.parse(stored) as QuoteData;
  } catch (error) {
    console.error('Error retrieving quote from localStorage:', error);
    return null;
  }
}

/**
 * Get any quote from localStorage (since we only keep one quote)
 * Useful as a fallback when cardId might not match
 * @returns QuoteData and the cardId it was stored with, or null if no quote exists
 */
export function getAnyQuote(): { quote: QuoteData; cardId: string } | null {
  try {
    const quoteKeys = getAllQuoteKeys();
    
    if (quoteKeys.length === 0) {
      return null;
    }
    
    // Get the first quote (we only keep one anyway)
    const key = quoteKeys[0];
    const stored = localStorage.getItem(key);
    
    if (!stored) {
      return null;
    }
    
    // Extract cardId from key (remove 'quote_' prefix)
    const cardId = key.replace(QUOTE_KEY_PREFIX, '');
    const quote = JSON.parse(stored) as QuoteData;
    
    return { quote, cardId };
  } catch (error) {
    console.error('Error retrieving any quote from localStorage:', error);
    return null;
  }
}

/**
 * Remove quote data from localStorage
 */
export function clearQuote(cardId: string): void {
  try {
    const key = getQuoteKey(cardId);
    localStorage.removeItem(key);
  } catch (error) {
    console.error('Error clearing quote from localStorage:', error);
  }
}

/**
 * Remove all quotes from localStorage
 * Useful for resetting state or cleanup
 */
export function cleanupAllQuotes(): void {
  try {
    const keysToRemove = getAllQuoteKeys();
    let removedCount = 0;
    
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      removedCount++;
    });
  } catch (error) {
    console.error('Error cleaning up quotes from localStorage:', error);
  }
}

/**
 * Ensure only one quote exists in localStorage
 * If multiple quotes exist, keeps the first one found and removes the rest
 * Can be called on app initialization or periodically to maintain clean state
 */
export function ensureSingleQuote(): void {
  try {
    const quoteKeys = getAllQuoteKeys();
    
    if (quoteKeys.length <= 1) {
      return;
    }

    // Multiple quotes found - keep the first one, remove the rest
    console.warn(`⚠️ Found ${quoteKeys.length} quotes in localStorage. Keeping the first one and removing the rest.`);
    
    const quoteToKeep = quoteKeys[0];
    const quotesToRemove = quoteKeys.slice(1);
    
    quotesToRemove.forEach(key => {
      localStorage.removeItem(key);
    });
  } catch (error) {
    console.error('Error ensuring single quote in localStorage:', error);
  }
}


